<template>
  <span
    class="toolkit-mark"
    :class="[
      `toolkit-mark--${size}`,
      `toolkit-mark--${theme}`,
      showLogo ? 'toolkit-mark--logo' : 'toolkit-mark--monogram',
    ]"
    :style="markStyle"
    :title="name"
  >
    <img
      v-if="showLogo"
      :src="logoUrl"
      :alt="name"
      class="toolkit-mark__img"
      @error="onLogoError"
    />
    <template v-else>{{ initial }}</template>
  </span>
</template>

<script>
// Renders a toolkit mark as either a real brand logo (preferred) or a
// brand-tinted monogram fallback. Logos source: `logo_url` from
// composio.toolkits.list() in production; Simple Icons CDN URLs in the demo.
//
// PALETTE entries are kept for the monogram fallback path: when a logo is
// missing or fails to load, the tile still picks up brand-relevant color so
// it doesn't drop to a generic grey.
const PALETTE = {
  hubspot:          { bg: '#fff1ec', fg: '#e8551f', border: '#fbd6c7' },
  linear:           { bg: '#eef0fa', fg: '#5e6ad2', border: '#cfd5f0' },
  stripe:           { bg: '#ece9fb', fg: '#6259cf', border: '#cdc7ee' },
  github:           { bg: '#f2f2f4', fg: '#1f2328', border: '#d8dadd' },
  notion:           { bg: '#f5f3ef', fg: '#1f1d18', border: '#dad6cd' },
  google_sheets:    { bg: '#e6f4ec', fg: '#137a3d', border: '#bfe2cd' },
  google_analytics: { bg: '#fff4e3', fg: '#cc7a00', border: '#f5dbab' },
  mixpanel:         { bg: '#efe9fb', fg: '#7038c7', border: '#d6c5f1' },
  salesforce:       { bg: '#e6f1fb', fg: '#0577c0', border: '#bcdaef' },
  jira:             { bg: '#e7eefc', fg: '#1455d6', border: '#bfd2f4' },
  zendesk:          { bg: '#e6f1ed', fg: '#03635d', border: '#bcd9d2' },
  intercom:         { bg: '#e3edff', fg: '#1466e0', border: '#b9d2f8' },
  slack:            { bg: '#fdeff5', fg: '#a4194e', border: '#f3c8d8' },
  default:          { bg: '#f2ecfb', fg: '#6c30cc', border: '#d8c6f0' },
}

export default {
  name: 'AutomationToolkitMark',
  props: {
    slug: { type: String, default: '' },
    name: { type: String, default: '' },
    size: { type: String, default: 'md' }, // sm | md | lg
    theme: { type: String, default: 'tile' }, // tile | bare
    logoUrl: { type: String, default: '' },
  },
  data() {
    return { logoFailed: false }
  },
  computed: {
    palette() { return PALETTE[this.slug] || PALETTE.default },
    showLogo() { return !!this.logoUrl && !this.logoFailed },
    initial() {
      const src = (this.name || this.slug || '?').replace(/_/g, ' ').trim()
      return src.charAt(0).toUpperCase()
    },
    markStyle() {
      if (this.theme === 'bare') {
        return { background: 'transparent', color: this.palette.fg, borderColor: 'transparent' }
      }
      // Real logos read better on a neutral plate so brand colors don't fight
      // the tinted background. Monograms keep the brand tint for color cues.
      if (this.showLogo) {
        return { background: '#ffffff', color: this.palette.fg, borderColor: '#e2e4ea' }
      }
      return { background: this.palette.bg, color: this.palette.fg, borderColor: this.palette.border }
    },
  },
  watch: {
    logoUrl() { this.logoFailed = false },
  },
  methods: {
    onLogoError() { this.logoFailed = true },
  },
}
</script>

<style scoped>
.toolkit-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 6px;
  font-family: 'Lato', sans-serif;
  font-weight: 900;
  letter-spacing: 0;
  flex-shrink: 0;
  overflow: hidden;
}
.toolkit-mark--sm { width: 18px; height: 18px; font-size: 11px; border-radius: 4px; }
.toolkit-mark--md { width: 24px; height: 24px; font-size: 13px; border-radius: 4px; }
.toolkit-mark--lg { width: 36px; height: 36px; font-size: 18px; border-radius: 8px; }

.toolkit-mark__img {
  display: block;
  max-width: 70%;
  max-height: 70%;
  object-fit: contain;
}
</style>
